import { groq } from 'next-sanity'
import { getClient } from './sanity'

export const textQuery = groq`
  ...,
  markDefs[] {
    ...,
    _type == 'definition' => {
      _key,
      _type,
      "title": definitionRef->title,
      "content": definitionRef->text,
    },
    _type == 'internalLink' => {
      _key,
      _type,
      "target": {
        "slug": @.reference->slug.current,
        "_type": @.reference->_type,
      },
    },
  },
`

export const IMAGE_FIELDS = groq`{
  ...,
  "dimensions": asset->metadata.dimensions,
  "lqip": asset->metadata.lqip,
  "palette": asset->metadata.palette,
  "hasAlpha": asset->metadata.hasAlpha,
}`

export const VIDEO_FIELDS = groq`{
  ...,
  videoType == "youtube" => {
    "youtubeId": id,
  },
  videoType == "internal" => {
    "playbackId": video.video.asset->playbackId,
    "video": video.video {
      ...,
      asset->{
        ...,
      },
    }
  },
}`

export const topicThemeQuery = groq`{
  _id,
  "light": colors.light,
  "background": colors.background,
  "promoted": colors.promoted,
  "dark": colors.dark,
  "illustration": {
    "aspect": illustration.png.asset->metadata.dimensions.aspectRatio,
    "path": illustration.png.asset->url,
    "webp": illustration.webp.asset->url
  }
}`

const collaboratorsQuery = groq`
  collaborators[]{
    _key,
    ...@->{
      title,
      href
    }
  }
`

export const resourceQuery = groq`{
  excludeFromSearch,
  title,
  'slug': slug.current,
  preamble,
  typeTag,
  image {
    alt,
    'dimensions': asset->metadata.dimensions,
    'url': asset->url
  },
  file {
    ...,
    asset-> {
      ...
    }
  },
  'lessonRef': *[_type == 'lesson' && references(^._id)] {
    topic->{
      _type,
      title,
      'illustration': illustration.webp.asset->
    },
    title,
    _id,
    'slug': slug.current,
    types
  }
}`

export const lessonQuery = (limited = false) => groq`{
  _id,
  _type,
  publishedAt,
  _updatedAt,
  updatedAt,
  title,
  purpose,
  preamble,
  duration,
  ogImages,
  types,
  excludeFromSearch,
  'subjects':
      *[_type == "subject" && _id in array::unique(^.competencyGoals[]->competencyGoalSets[]->plan->{
        "subject": *[_type == "subject" && references(^._id)]
      }.subject[]._id)][] {
        title,
        'value': slug.current,
        'grade': {
          'vgs':  count(^.competencyGoals[]->competencyGoalSets[]->usedInGrades[][@ in ['vg1', 'vg2', 'vg3', 'paabygning_generell_studiekompetanse']]) > 0,
          'grunnskole': count(^.competencyGoals[]->competencyGoalSets[]->usedInGrades[][@ in ['aarstrinn8', 'aarstrinn9', 'aarstrinn10']]) > 0,
        }
      },
  topic->{
    title,
    "slug": slug.current,
    ${
      limited !== true
        ? `
    "lessons": *[
      _type=='lesson' &&
      references(^._id) &&
      type != 'intro' &&
      (!defined(types.intro) || types.intro == false)
    ] {
      _id,
      types,
      title,
      duration,
      'slug': slug.current,
      "topicTheme": ^->${topicThemeQuery}
    },`
        : ''
    }
  },
  "slug": slug.current,
  ${
    limited !== true
      ? `sections[] {
    "_key": sections[0]._key,
    "title": sections[0].title,
    sections[] {
      ...,
      text[] {
        ${textQuery}
      },
      extra[] {
        ...,
        blocks[] {
          ...,
          children,
          markDefs[] {
            ...,
            'internalLink': {
              "_type": reference->_type,
              "slug": reference->slug.current
            }
          },
        }
      },
      media[]{
        ...,
        _type == 'image' => ${IMAGE_FIELDS},
        _type == 'video' => @-> {
          ...,
          "video": video.video {
            ...,
            asset->,
          },
        },
        _type == 'muxVideo' => {
          ...,
          video {
            ...,
            asset->,
          },
        },
      },
      assets[]{
        ...,
        _type == 'internalResource' => {
          'entry': entry->{
            _type,
            title,
            'slug': slug.current,
          }
        },
        _type == 'file' => {
          asset->{
            size,
            extension,
            mimeType,
            url,
          }
        },
        _type == 'resource' => {
          "title": resource->title,
          "asset": resource->file.asset->{
            size,
            extension,
            mimeType,
            url
          }
        }
      }
    }
  },`
      : ''
  }
  competencyGoals[]->{
    _id,
    title,
    "planTitle": plans[0]->{
      "title": coalesce(shortTitle, title)
    },
    "interdisciplinarySkills": interdisciplinarySkills[]->{
      code,
      title
    },
    "coreElements": coreElements[]->title,
    "competencyGoalSetTitle": competencyGoalSets[0]->shortTitle
  },
  "topicTheme": topic->${topicThemeQuery},
  "collaborators": ${collaboratorsQuery}
}`

export const presentationQuery = `{
  ...,
  slides[] {
    ...,
    image${IMAGE_FIELDS}
  }
}`

export const interactiveTaskQuery = `{
  ...,
  "topicTheme": topicTheme->${topicThemeQuery},
}`

export const quizQuery = `{
  ...,
  questions[] {
    ...,
    media[0] {
      ...,
      _type == 'image' => ${IMAGE_FIELDS},
      _type == 'video' => @-> {
        ...,
        "video": video.video {
          ...,
          asset->,
        },
      },
    },
  },
}`

export const articleQuery = `{
  ...,
  image${IMAGE_FIELDS},
  publishedAt,
  _updatedAt,
  updatedAt,
  loanArticle,
  loanArticleSource,
  "author": authors[0]->{
    title,
    role,
  },
  text[] {
    ${textQuery}
    _type == 'image' => ${IMAGE_FIELDS},
    _type == 'gallery' => {
      ...,
      asset->,
      images[]${IMAGE_FIELDS}
    }
  }
}`

export const topicQuery = groq`{
  _id,
  title,
  preamble,
  "slug": slug.current,
  ogImages,
  promotedLesson->{
    _id,
    title,
    purpose,
    duration,
    'slug': slug.current
  },
  introduction->{
    ...,
    sections[]{
      ...,
      text[] {
        ${textQuery}
      },
      asset->{
        _id,
        _type,

        _type == "article" => {
          title,
          image,
          publishedAt,
          "slug": slug.current,
        },

        _type == "resource" => {
          title,
          preamble,
          image,
          file {
            asset->{
              extension,
              mimeType,
              size,
              url
            }
          },
        },

        _type == "quiz" => {
          title,
          "questions": count(questions),
          "slug": slug.current,
        },

        _type == "presentation" => {
          title,
          "slides": count(slides),
          "slug": slug.current,
        },

        _type == "picture" => {
          image ${IMAGE_FIELDS},
        },

        _type == "talkingType" => {
          _id,
          title,
          language,
          talkingCardTranslations->{...},
          "slug": slug.current,
          "topicTheme": topicTheme->${topicThemeQuery}
        },

        _type == "video" => {
          ...,
          video {
            ...,
            title,
            description,
            caption,
            video {
              ...,
              asset->{
                ...,
              }
            },
          }
        }
      }
    },
  }{
    ...,
    "auxhilary": {
      "article":  *[_type == "article" && _id in ^.sections[].asset._id]${articleQuery},
      "quiz": *[_type == "quiz" && _id in ^.sections[].asset._id]${quizQuery},
      "presentation": *[_type == "presentation" && _id in ^.sections[].asset._id]${presentationQuery}
    }
  },
  "lessonOrder": lessonOrder[]._ref,
  "lessons": *[
    _type=='lesson' &&
    references(^._id) &&
    type != 'intro' &&
    (!defined(types.intro) || types.intro == false)
  ] {
    _id,
    title,
    purpose,
    duration,
    types,
    'slug': slug.current
  },
  "resources": *[
    references(^._id) &&
    _type != 'lesson' &&
    _type != 'student' &&
    _type != 'course'
  ][count(topic[references(^.^._id)]) > 0] {
    _type == "presentation" => {
      "slides": count(slides),
      "slug": slug.current,
    },

    _type == "subTopic" => {
      image${IMAGE_FIELDS},
      preamble,
    },

    _type == "article" => {
      ...,
      image${IMAGE_FIELDS},
    },

    _type == "resource" => {
      "file": file.asset->url,
      typeTag,
    },

    _type == "video" => {
      ...,
      videoType == "youtube" => {
        "youtubeId": id,
      },
      videoType == "internal" => {
        "video": video.video {
          ...,
          asset->{
            ...,
          },
        }
      },
    },

    title,
    _type,
    'slug': slug.current,

  },
  "topicTheme": ${topicThemeQuery}
}`

export const videoQuery = groq`{
  ...,
  title,
  defined(description[0]) => {
    description[]{
      ...,
      markDefs[] {
        ...,
        _type == 'definition' => {
          _key,
          _type,
          "title": definitionRef->title,
          "content": definitionRef->text,
        },
        'internalLink': {
          "_type": reference->_type,
          "slug": reference->slug.current
        }
      },
    },
  },
  !defined(description[0]) => {
    description,
  },
  caption,
  videoType,
  publishedAt,
  _updatedAt,
  "slug": slug.current,
  "video": select(videoType == 'internal' => video{..., "videoFile": video{..., asset->}}, videoType == 'youtube' => id),
    'lessonRef': *[_type == 'lesson' && references(^._id)]{
    topic->{
      _type,
      title,
      'illustration': illustration.webp.asset->
    },
    title,
    _id,
    'slug': slug.current,
  }
}`

export const frontpageQuery = `{
  _id,
  title,
  content[] {
    ...,

    _type == 'resources' => {
      items[]->{
        _id,
        _type,
        title,
        "slug": slug.current,
        "topicTheme": topic->${topicThemeQuery},

        _type == "lesson" => {
          purpose,
          duration,
          types,
        },
        _type == "quiz" => {
          title,
          "questions": count(questions),
          "slug": slug.current,
        },
        _type == "subTopic" => {
          image${IMAGE_FIELDS},
          preamble,
        },
        _type == "talkingType" => {
          _id,
          title,
          language,
          talkingCardTranslations->{...},
          "slug": slug.current,
          "topicTheme": topicTheme->${topicThemeQuery}
        },
        _type == "presentation" => {
          "slides": count(slides),
          "slug": slug.current,
        },
        _type == "quiz" => {
          "slides": count(slides),
          "slug": slug.current,
          "topicTheme": topic[0]->${topicThemeQuery},
        },
        _type == "resource" => {
          preamble,
          image,
          "file": file.asset->url,
          typeTag,
        },
        _type == "interactiveTask" => {
          "topicTheme": topicTheme->${topicThemeQuery}
        },
        _type == "video" => {
          "topicTheme": topic[0]->${topicThemeQuery},
          videoType == "youtube" => { "youtubeId": id },
          videoType == "internal" => {
            "video": video.video {
              ...,
              asset->{ ... },
            }
          },
        },
      },
    },
    _type == 'promotion' => {
      title,
      preamble,
      image${IMAGE_FIELDS},
      "reference": link.reference->{
        _type,
        title,
        topicTheme->${topicThemeQuery},
        image${IMAGE_FIELDS},
        "slug": slug.current
      },
      "external": link.external,
    }
  },
  "topicThemes": *[_type == 'topic']${topicThemeQuery}
}`

export const aboutQuery = `{
  ...,
  image${IMAGE_FIELDS},
  text[]{
    ${textQuery}
  },
  employees[]->{
    ...,
    image${IMAGE_FIELDS}
  },
  previousEmployees[]->{
    ...,
    image${IMAGE_FIELDS}
  },
}`

export const resourcesQuery = groq`{
  ...,
  staticPagesData {
    ...,
    staticPages[]-> {
      _id,
      _type,
      publishedAt,
      "slug": slug.current,
      title,
      image${IMAGE_FIELDS},
      preamble
    },
  },
  articlesData {
    ...,
    articles[] {
      ...,
      image${IMAGE_FIELDS},
      _type == 'reference' => @-> {
        _id,
        _type,
        publishedAt,
        "slug": slug.current,
        title,
        image${IMAGE_FIELDS},
        loanArticle,
        loanArticleSource
      },
    },
  },
    videosData {
    ...,
     videos [] {
      ...,
      _type == 'reference' => @-> {
      ...
    }
    }
  },
  articles[] {
    ...,
    image${IMAGE_FIELDS},
    _type == 'reference' => @-> {
      _id,
      _type,
      publishedAt,
      "slug": slug.current,
      title,
      image${IMAGE_FIELDS},
      loanArticle,
      loanArticleSource
    },
  },
  externalArticlesData {
    ...,
    externalArticles[]{
      ...,
      file{
        asset->{
          extension,
          mimeType,
          size,
          url
        }
      },
      image${IMAGE_FIELDS}
    }
  },
  externalArticles[]{
    ...,
    file{
      asset->{
        extension,
        mimeType,
        size,
        url
      }
    },
    image${IMAGE_FIELDS}
  }
}`

export const talkingCardQuery = groq`{
  _id,
  title,
  collaberator->,
  backLink,
  excludeFromSearch,
  "slug": slug.current,
  "topicTheme": topicTheme->${topicThemeQuery},
  "cards": *[_type=='talkingCard' && references(^._id)] {
    _id,
    title,
    richText,
    type,
    textOrImage,
    image${IMAGE_FIELDS},
  },
  instructions->{...},
  logo${IMAGE_FIELDS},
  talkingCardTranslations->{...},
  language
}`

export const staticPageQuery = groq`{
  ...,
  "topicTheme": topicTheme->${topicThemeQuery},
  image ${IMAGE_FIELDS},
  video {
    ...,
    "mp4": mp4.asset->{
      mimeType,
      url,
    },
    "webm": webm.asset->{
      mimeType,
      url,
    },
  },
  sections[]{
    ...,
    cards[]{
      _key,
      _type,
      _type == "link" => {
        ...,
        image ${IMAGE_FIELDS},
      },
      _type == "image" => {
        assets[]{
          ...,
          _type == 'internalResource' => {
            'entry': entry->{
              _type,
              title,
              'slug': slug.current,
            }
          },
          _type == 'file' => {
            asset->{
              size,
              extension,
              mimeType,
              url,
            }
          },
          _type == 'resource' => {
            "title": resource->title,
            "asset": resource->file.asset->{
              size,
              extension,
              mimeType,
              url
            }
          }
        },
        facsimile,
        image ${IMAGE_FIELDS},

      },
      _type == "sectionCard" => {
        ...,
        text[] {
          ${textQuery}
        },
        extra[] {
          ...,
          blocks[] {
            ...,
            children,
            markDefs[] {
              ...,
              'internalLink': {
                "_type": reference->_type,
                "slug": reference->slug.current
              }
            },
          }
        },
        media[]{
          ...,
          _type == 'image' => ${IMAGE_FIELDS},
          _type == 'video' => @-> {
            ...,
            "video": video.video {
              ...,
              asset->,
            },
          },
        },
        assets[]{
          ...,
          _type == 'internalResource' => {
            'entry': entry->{
              _type,
              title,
              'slug': slug.current,
            }
          },
          _type == 'file' => {
            asset->{
              size,
              extension,
              mimeType,
              url,
            }
          },
          _type == 'resource' => {
            "title": resource->title,
            "asset": resource->file.asset->{
              size,
              extension,
              mimeType,
              url
            }
          },
        }
      },
      ...@->{
        _type,
        _type == "video" => ${VIDEO_FIELDS},
        _type == "talkingType" => {
          _id,
          title,
          language,
          talkingCardTranslations->{...},
          "slug": slug.current,
          "topicTheme": topicTheme->${topicThemeQuery}
        },
        _type == 'quiz' => {
          title,
          'slug': slug.current,
        },
        _type == 'presentation' => {
          title,
          'slug': slug.current,
        },
        _type == 'resource' => {
          title,
          'slug': slug.current,
          'fileExtension': file.asset->extension,
        },

        _type == "lesson" => {
          _id,
          title,
          purpose,
          duration,
          types,
          topic->{
            title,
            "slug": slug.current
          },
          "topicTheme": topic->${topicThemeQuery},
          "slug": slug.current,
        },
        _type == "article" => {
          _id,
          "slug": slug.current,
          title,
          image${IMAGE_FIELDS},
          preamble,
          publishedAt,
          date,
          source,
          level
        }
      }
    }
  },
  "collaborators": ${collaboratorsQuery},
  backLink,
}`

export async function getAllLessons() {
  return getClient().fetch(`
    *[_type == "lesson" && !(_id in path('drafts.**'))]${lessonQuery()}
  `)
}

export async function getAllPresentationsWithSlug() {
  return getClient().fetch(`
    *[_type == "presentation"] { 'slug': slug.current }
  `)
}

export async function getAllQuizWithSlug() {
  return getClient().fetch(`
    *[_type == "quiz"] { 'slug': slug.current }
  `)
}

export async function getAllInteractiveTasksWithSlug() {
  return getClient().fetch(`
    *[_type == "interactiveTask"] { 'slug': slug.current }
  `)
}

export async function getAllArticlesWithSlug() {
  return getClient().fetch(`
    *[_type == "article"] { 'slug': slug.current }
  `)
}

export async function getFrontpage() {
  return getClient().fetch(`*[_type == "frontpage"]${frontpageQuery}[0]`)
}

export async function getUrlList() {
  return getClient().fetch(`
  [
    {"url": ""},
    {"url": "om-oss"}
  ] + *[
      !(_id in path("drafts.**"))
      && _type in [
        'article',
        'presentation',
        'quiz',
        'topic',
        'lesson',
        'page',
        'video',
        'interactiveTask',
        'subTopic',
      ]
      && defined(slug.current)
      && excludeFromSearch != true
    ] {
      "url": select(
        _type == "lesson" => "undervisningsopplegg/",
        _type == 'article' => "artikkel/",
        _type == 'student' => "elevopplegg/",
        _type == 'quiz' => "quiz/",
        _type == 'presentation' => "presentasjon/",
        _type == 'topic' => "tema/",
        _type == 'page' => "",
        _type == 'video' => "video/",
        _type == 'subTopic' => "samling/",
        _type == 'interactiveTask' => "oppgave/",
        _id
      ) + slug.current,
      _updatedAt,
      _type,
      "priority": select(
        _type == "lesson" => "0.9",
        _type == "subTopic" => "0.8",
        _type == 'article' => "0.8",
        _type == 'student' => "0.1",
        _type == 'quiz' => "0.7",
        _type == 'presentation' => "0.2",
        _type == 'interactiveTask' => "0.2",
        _type == 'video' => "0.2",
        _type == 'topic' => "0.3",
        _type == 'page' => "0.8",
      )
    }`)
}

export async function getSettings(preview) {
  return getClient(preview).fetch(`// groq
    *[_id == "settings"][0]{
      menu[]{
        _key,
        _type,
        title,
        "href": "/" + select(
          type == "internalRoute" => internalRoute,
          type == "dynamicPage" => dynamicPage->slug.current
        )
      },
      searchPlaceholders[],
      footer {
        ...,
      },
      "topics": *[_type == "topic"] { // && isHidden != true
        _id,
        'slug': slug.current,
        title,
        preamble,
        "topicTheme": ${topicThemeQuery}
      },
    }
  `)
}
